/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { FC } from "react";
import type { TFunction } from "i18next";
import { z } from "zod";
import type {
  FormStep1EnergyConsumptionResponse,
  FormStep2OperatorAndTariffResponse,
  FormStep3EnergyBillingResponse,
  FormStep4ConnectionAndInstallationResponse,
  FormStep5CustomerResponse,
  StepKey,
  StepTitle,
} from "@/types/Form";
import type { Voivodeship } from "@/types/Voivodeship";
import {
  kwhToMwh,
  kwToMw,
  mwhToKwh,
  mwToKw,
  plnPerKwhToPlnPerMwh,
  plnPerMwhToPlnPerKwh,
} from "@/utils/units";
import * as Steps from "@/components/FormSteps";
import { getBooleanEnumString, getBooleanEnumValue } from "./booleanEnum";
import { getStep1Schema, type Step1Schema } from "./step1Schema";
import { getStep2Schema, type Step2Schema } from "./step2Schema";
import { getStep3Schema, type Step3Schema } from "./step3Schema";
import { getStep4Schema, type Step4Schema } from "./step4Schema";
import { getStep5Schema, type Step5Schema } from "./step5Schema";
import { getSummarySchema } from "./summarySchema";

export interface FormStep {
  title: StepTitle;
  component: FC;
  buttonText?: string;
}

export type MultiStepFormType = {
  step1: Step1Schema;
  step2: Step2Schema;
  step3: Step3Schema;
  step4: Step4Schema;
  step5: Step5Schema;
  summary: z.infer<ReturnType<typeof getSummarySchema>>;
};

export type StepApiResponse = {
  step1: FormStep1EnergyConsumptionResponse;
  step2: FormStep2OperatorAndTariffResponse;
  step3: FormStep3EnergyBillingResponse;
  step4: FormStep4ConnectionAndInstallationResponse;
  step5: FormStep5CustomerResponse;
  summary: never;
};

export type StepApiPayload = {
  step1: ReturnType<typeof mapStep1ToApi>;
  step2: ReturnType<typeof mapStep2ToApi>;
  step3: ReturnType<typeof mapStep3ToApi>;
  step4: ReturnType<typeof mapStep4ToApi>;
  step5: ReturnType<typeof mapStep5ToApi>;
  summary: never;
};

type BaseConfig<K extends StepKey> = {
  title: StepTitle;
  stepKey: K;
  buttonText?: "iWantToReceiveFreeAnalysis" | "next";
  component: FC;
};

type StepConfigMap = {
  step1: BaseConfig<"step1"> & {
    mapToForm: (d: StepApiResponse["step1"]) => MultiStepFormType["step1"];
    mapToApi: (v: MultiStepFormType["step1"]) => StepApiPayload["step1"];
  };
  step2: BaseConfig<"step2"> & {
    mapToForm: (d: StepApiResponse["step2"]) => MultiStepFormType["step2"];
    mapToApi: (v: MultiStepFormType["step2"]) => StepApiPayload["step2"];
  };
  step3: BaseConfig<"step3"> & {
    mapToForm: (d: StepApiResponse["step3"]) => MultiStepFormType["step3"];
    mapToApi: (v: MultiStepFormType["step3"]) => StepApiPayload["step3"];
  };
  step4: BaseConfig<"step4"> & {
    mapToForm: (d: StepApiResponse["step4"]) => MultiStepFormType["step4"];
    mapToApi: (v: MultiStepFormType["step4"]) => StepApiPayload["step4"];
  };
  step5: BaseConfig<"step5"> & {
    mapToForm: (d: StepApiResponse["step5"]) => MultiStepFormType["step5"];
    mapToApi: (v: MultiStepFormType["step5"]) => StepApiPayload["step5"];
  };
  summary: BaseConfig<"summary"> & {
    mapToForm: () => MultiStepFormType["summary"];
  };
};

export type StepConfig = StepConfigMap[keyof StepConfigMap];
export type StepConfigs = { [K in StepKey]: StepConfigMap[K] };

const mapStep1FromApi = (d: FormStep1EnergyConsumptionResponse): Step1Schema => ({
  energyProfileMethod: d.profile_completion_form,
  predefinedProfile: d.predefined_profile ?? undefined,
  tableData: d.table_input?.map(value => kwToMw(value) ?? "") ?? undefined,
  consumptionType: d.usage_timeframe ?? undefined,
  consumption: kwhToMwh(d.usage_timeframe_value) ?? undefined,
  excelProfile: d.file_input ?? null,
});

const mapStep1ToApi = (v: Step1Schema) => ({
  profile_completion_form: v.energyProfileMethod,
  table_input: v.tableData?.map(value => Number(mwToKw(value) ?? value)) || null,
  file_input: v.excelProfile instanceof File ? v.excelProfile : null,
  predefined_profile: v.predefinedProfile || null,
  usage_timeframe: v.consumptionType || null,
  usage_timeframe_value: mwhToKwh(v.consumption),
});

const mapStep2FromApi = (d: FormStep2OperatorAndTariffResponse): Step2Schema => ({
  operator: d.operator,
  tariff: d.tariff,
});

const mapStep2ToApi = (v: Step2Schema) => ({
  operator_id: v.operator?.id || 0,
  tariff_id: v.tariff?.id || 0,
});

const mapStep3FromApi = (d: FormStep3EnergyBillingResponse): Step3Schema => ({
  billingType: d.energy_settlement_type,
  margin: plnPerKwhToPlnPerMwh(d.margin_rate) ?? undefined,
  fixedFee: d.fixed_monthly_fee ?? undefined,
  fixedPrice: d.fixed_price ?? undefined,
  tariff: d.tariff ?? undefined,
});

const normalizeDecimalForApi = (value: string | null, decimalPlaces: number): string | null => {
  if (value === null || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric.toFixed(decimalPlaces);
};

const mapStep3ToApi = (v: Step3Schema) => ({
  energy_settlement_type: v.billingType,
  margin_rate: normalizeDecimalForApi(plnPerMwhToPlnPerKwh(v.margin), 6),
  fixed_monthly_fee: v.fixedFee || null,
  fixed_price: v.fixedPrice || null,
  tariff_id: v.tariff?.id || null,
});

const mapStep4FromApi = (d: FormStep4ConnectionAndInstallationResponse): Step4Schema => ({
  importPower: kwToMw(d.connection_condition_import) ?? undefined,
  exportPower: kwToMw(d.connection_condition_export) ?? undefined,
  canSellEnergy: getBooleanEnumString(d.is_possible_to_sell_excess_energy),
  hasPvInstallation: getBooleanEnumString(d.has_photovoltaic_installation),
  installationPower: kwToMw(d.installation_capacity) ?? "",
  inverterPower: kwToMw(d.inverter_power) ?? "",
  voivodeship: d.voivodeship ?? undefined,
});

const mapStep4ToApi = (v: Step4Schema) => ({
  connection_condition_import: mwToKw(v.importPower),
  connection_condition_export: mwToKw(v.exportPower),
  is_possible_to_sell_excess_energy: getBooleanEnumValue(v.canSellEnergy),
  has_photovoltaic_installation: getBooleanEnumValue(v.hasPvInstallation),
  installation_capacity: mwToKw(v.installationPower),
  inverter_power: mwToKw(v.inverterPower),
  voivodeship: v.voivodeship ? (v.voivodeship as Voivodeship) : null,
});

const mapStep5FromApi = (d: FormStep5CustomerResponse): Step5Schema => ({
  companyName: d.company,
  firstName: d.first_name,
  lastName: d.last_name,
  email: d.email,
  phone: d.phone ?? "",
  privacyPolicyRodo: getBooleanEnumString(d.privacy_policy_rodo),
  marketingEmailConsent: getBooleanEnumString(d.marketing_email_consent),
  marketingPhoneConsent: getBooleanEnumString(d.marketing_phone_consent),
  newsletterConsent: getBooleanEnumString(d.newsletter_consent),
  dataProcessingConsent: getBooleanEnumString(d.data_processing_consent),
  dataAccuracyResponsibilityConsent: getBooleanEnumString(d.data_processing_consent),
});

export const mapStep5ToApi = (values: Step5Schema) => ({
  company: values.companyName,
  first_name: values.firstName,
  last_name: values.lastName,
  email: values.email,
  phone: values.phone || null,
  privacy_policy_rodo: getBooleanEnumValue(values.privacyPolicyRodo),
  marketing_email_consent: getBooleanEnumValue(values.marketingEmailConsent),
  marketing_phone_consent: getBooleanEnumValue(values.marketingPhoneConsent),
  newsletter_consent: getBooleanEnumValue(values.newsletterConsent),
  data_processing_consent: getBooleanEnumValue(values.dataProcessingConsent),
  data_accuracy_responsibility_consent: getBooleanEnumValue(
    values.dataAccuracyResponsibilityConsent,
  ),
});

export const getStepConfigs = (): StepConfigs => ({
  step1: {
    title: "step1",
    stepKey: "step1",
    component: Steps.Step1,
    mapToForm: mapStep1FromApi,
    mapToApi: mapStep1ToApi,
  },
  step2: {
    title: "step2",
    stepKey: "step2",
    component: Steps.Step2,
    mapToForm: mapStep2FromApi,
    mapToApi: mapStep2ToApi,
  },
  step3: {
    title: "step3",
    stepKey: "step3",
    component: Steps.Step3,
    mapToForm: mapStep3FromApi,
    mapToApi: mapStep3ToApi,
  },
  step4: {
    title: "step4",
    stepKey: "step4",
    component: Steps.Step4,
    mapToForm: mapStep4FromApi,
    mapToApi: mapStep4ToApi,
  },
  step5: {
    title: "step5",
    stepKey: "step5",
    component: Steps.Step5,
    mapToForm: mapStep5FromApi,
    mapToApi: mapStep5ToApi,
    buttonText: "iWantToReceiveFreeAnalysis",
  },
  summary: {
    title: "step6",
    stepKey: "summary",
    component: Steps.Step6,
    mapToForm: () => ({}) as MultiStepFormType["summary"],
  },
});

export const getMultiStepFormSchema = (t: TFunction) =>
  z.object({
    step1: getStep1Schema(t),
    step2: getStep2Schema(t),
    step3: getStep3Schema(t),
    step4: getStep4Schema(t),
    step5: getStep5Schema(t),
    summary: getSummarySchema(t),
  });

export const getFormSteps = (t: TFunction) => {
  const configs = getStepConfigs();

  return Object.values(configs).map(c => ({
    title: c.title,
    component: c.component,
    buttonText: t(typeof c.buttonText === "string" ? c.buttonText : "next"),
  }));
};
